import { Component, inject, LOCALE_ID } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { HolidaysService } from '../../../shared/services/holidays/holidays.service';
import { ProtocolImportResponse } from '../../../shared/interfaces/holidays.interface';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { MatDivider } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_NATIVE_DATE_FORMATS,
  NativeDateAdapter,
} from '@angular/material/core';
import {
  ProtocolDialogComponent,
  RuleGroupDialogResult,
} from '../../../shared/components/vacations/protocol-dialog/protocol-dialog.component';
import { DeleteComponent } from 'src/app/shared/components/delete/delete.component';

registerLocaleData(localeEs);

// Custom date adapter that starts weeks on Monday
class SpanishDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1; // Monday
  }
}

export interface Rule {
  name: string;
  rule_type: string;
  value?: number;
  boolean_value?: boolean;
  error_message?: string;
  date_range_start?: string;
  date_range_end?: string;
  is_determinant?: boolean;
}

export interface RuleGroup {
  name: string;
  operator: 'AND' | 'OR';
  priority: number;
  exempt_weeks?: number;
  days_entitled?: number;
  is_determinant?: boolean;
  rules: Rule[];
  child_groups: RuleGroup[];
}

@Component({
  selector: 'app-protocol-form',
  imports: [
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    FormsModule,
    RouterLink,
    LoaderComponent,
    MatDivider,
    MatExpansionModule,
    MatSelectModule,
    MatDatepickerModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
    { provide: LOCALE_ID, useValue: 'es-ES' },
    {
      provide: DateAdapter,
      useClass: SpanishDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
  ],
  templateUrl: './protocol-form.component.html',
  styleUrls: [
    './protocol-form.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class ProtocolFormComponent {
  // Mode management
  isEditMode: boolean = false;
  protocolId: number | null = null;

  // Common properties
  myroles: any = {};
  protocolForm: FormGroup;
  protocol: any = {};
  isSubmitting = false;
  attemptedSubmit = false;
  returnLink: string = '/home/vacations/drivers';
  breadcrumbTitle: string = 'Vacaciones chóferes';

  // Rule groups
  ruleGroups: RuleGroup[] = [];

  // Rule type choices
  ruleTypeChoices = [
    { value: 'max_weeks', label: 'Máximo de semanas permitidas' },
    { value: 'min_weeks', label: 'Mínimo de semanas requeridas' },
    { value: 'single_days', label: 'Días sueltos permitidos' },
    { value: 'blocked_range', label: 'Rango de fechas bloqueado' },
    { value: 'allowed_range', label: 'Rango permitido' },
    { value: 'min_consecutive_weeks', label: 'Mínimo de semanas consecutivas' },
    { value: 'max_consecutive_weeks', label: 'Máximo de semanas consecutivas' },
    { value: 'max_weeks_per_month', label: 'Máximo de semanas por mes' },
    {
      value: 'non_consecutive_weeks',
      label: 'Semanas no consecutivas requeridas',
    },
    {
      value: 'number_of_weeks_per_petition',
      label: 'Número máximo de semanas por petición',
    },
  ];

  // Operator choices
  operatorChoices = [
    { value: 'AND', label: 'Y (AND)' },
    { value: 'OR', label: 'O (OR)' },
  ];

  private _snackBar = inject(MatSnackBar);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private holidaysService: HolidaysService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private loaderService: LoaderService,
    private storageService: StorageService,
    private rolesService: RolesService,
    private dialog: MatDialog,
    private dateAdapter: DateAdapter<Date>
  ) {
    this.dateAdapter.setLocale('es-ES');
    this.protocolForm = this.fb.group({
      name: ['', [Validators.required]],
      code: ['', [Validators.required]],
      description: ['', [Validators.required]],
      enabled: [true],
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    this.returnLink = this.getReturnPath();
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isEditMode = true;
        this.protocolId = +params['id'];
        this.loaderService.showLoader();
        this.loadProtocolData();
      } else {
        this.isEditMode = false;
        this.protocolId = null;
      }
    });
    this.loadRoles();
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'Vacaciones',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      const permissionMessage = this.isEditMode
        ? 'No tienes permisos para editar un protocolo'
        : 'No tienes permisos para añadir un protocolo';

      this.rolesService.checkWritePermission(
        'Vacaciones',
        this.storageService,
        this._snackBar,
        this.router,
        this.returnLink,
        permissionMessage
      );
    }
  }

  private getReturnPath(): string {
    const currentUrl = this.router.url;
    const params = this.route.snapshot.params;

    // Check if we're in the protocols context
    if (currentUrl.includes('/protocols/protocol/')) {
      this.breadcrumbTitle = 'Protocolos';
      return '/home/vacations/protocols';
    }

    // Check if we're in the operational context (has opId, id, and year params)
    const opId = params['opId'];
    const id = params['id'];
    const year = params['year'];

    if (opId && id && year) {
      this.breadcrumbTitle = 'Vacaciones chóferes';
      return `/home/vacations/drivers/exercise/${id}/${year}/operational/${opId}`;
    }

    // Default: return to drivers list
    this.breadcrumbTitle = 'Vacaciones chóferes';
    return '/home/vacations/drivers';
  }

  private loadProtocolData(): void {
    if (!this.protocolId) return;

    this.holidaysService.getProtocolById(this.protocolId).subscribe({
      next: (data) => {
        this.protocol = data;

        // Set the form values from the loaded protocol data
        this.protocolForm.patchValue({
          name: this.protocol.name,
          code: this.protocol.code,
          description: this.protocol.description,
          enabled: this.protocol.enabled,
        });

        // Load rule groups for this protocol
        this.loadRuleGroups();
      },
      error: (error) => {
        this.loaderService.hideLoader();
        console.error('Error fetching protocol data', error);
        this.snackBar.open('Error al cargar los datos', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  private loadRuleGroups(): void {
    if (!this.protocolId) {
      this.loaderService.hideLoader();
      return;
    }

    this.holidaysService.getRuleGroupsByProtocol(this.protocolId).subscribe({
      next: (data) => {
        this.ruleGroups = this.transformFlatToNestedRuleGroups(data);
        this.loaderService.hideLoader();
      },
      error: (error) => {
        this.loaderService.hideLoader();
        console.error('Error fetching rule groups', error);
        this.snackBar.open('Error al cargar los grupos de reglas', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  private transformFlatToNestedRuleGroups(flatGroups: any[]): RuleGroup[] {
    // Create a map for quick lookup by id
    const groupMap = new Map<number, RuleGroup>();

    // First pass: create all groups with their rules
    flatGroups.forEach((group) => {
      const rules: Rule[] = (group.rule_memberships || []).map(
        (membership: any) => ({
          name: membership.rule_details?.name || '',
          rule_type: membership.rule_details?.rule_type || '',
          value: membership.rule_details?.value,
          boolean_value: membership.rule_details?.boolean_value,
          error_message: membership.rule_details?.error_message || '',
          date_range_start: membership.rule_details?.date_range_start,
          date_range_end: membership.rule_details?.date_range_end,
          is_determinant: membership.rule_details?.is_determinant || false,
        })
      );

      const ruleGroup: RuleGroup = {
        name: group.name,
        operator: group.operator as 'AND' | 'OR',
        priority: group.priority,
        exempt_weeks: group.exempt_weeks || 0,
        days_entitled: group.days_entitled || 0,
        is_determinant: group.is_determinant || false,
        rules: rules,
        child_groups: [],
      };

      groupMap.set(group.id, ruleGroup);
    });

    // Second pass: build hierarchy
    const rootGroups: RuleGroup[] = [];

    flatGroups.forEach((group) => {
      const ruleGroup = groupMap.get(group.id);
      if (!ruleGroup) return;

      if (group.parent_group === null) {
        // This is a root group
        rootGroups.push(ruleGroup);
      } else {
        // This is a child group, add to parent
        const parentGroup = groupMap.get(group.parent_group);
        if (parentGroup) {
          parentGroup.child_groups.push(ruleGroup);
        }
      }
    });

    // Sort by priority (higher priority first)
    rootGroups.sort((a, b) => b.priority - a.priority);

    return rootGroups;
  }

  saveChanges(): void {
    this.loaderService.showLoader();
    if (this.isSubmitting) return;
    this.attemptedSubmit = true;

    if (!this.protocolForm.valid) {
      this.loaderService.hideLoader();
      // Mark all controls as touched to trigger validation visuals
      Object.keys(this.protocolForm.controls).forEach((key) => {
        const control = this.protocolForm.get(key);
        control?.markAsTouched();
      });

      this.snackBar.open(
        'Por favor completa todos los campos requeridos',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    this.isSubmitting = true;
    this.importProtocol();
  }

  private importProtocol(): void {
    const formValue = this.protocolForm.value;

    const protocolJson = {
      protocol: {
        name: formValue.name || '',
        code: formValue.code || '',
        description: formValue.description || '',
        enabled: formValue.enabled ?? true,
        rule_groups: this.ruleGroups.map((group) =>
          this.transformRuleGroupForExport(group)
        ),
      },
    };

    this.holidaysService
      .importProtocol(protocolJson)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response: ProtocolImportResponse) => {
          this.loaderService.hideLoader();
          const message = response.stats.protocol_created
            ? 'Protocolo creado exitosamente'
            : 'Protocolo actualizado correctamente';
          this.snackBar.open(message, 'Cerrar', {
            duration: 3000,
          });
          this.router.navigate([this.returnLink]);
        },
        error: (error) => {
          this.loaderService.hideLoader();
          console.error('Error importing protocol:', error);
          const message = this.isEditMode
            ? 'Error al actualizar el protocolo'
            : 'Error al crear el protocolo';
          this.snackBar.open(message, 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  cancel(): void {
    this.router.navigate([this.returnLink]);
  }

  // Rule Group Management Methods
  addRuleGroup(): void {
    const dialogRef = this.dialog.open(ProtocolDialogComponent, {
      width: '700px',
      data: {
        title: 'Nuevo grupo de reglas',
        confirmText: 'Crear',
        cancelText: 'Cancelar',
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: RuleGroupDialogResult | null) => {
        if (result && result.action === 'save') {
          const newGroup: RuleGroup = {
            name: result.name,
            operator: result.operator,
            priority: 100 - this.ruleGroups.length * 10,
            exempt_weeks: result.exempt_weeks || 0,
            days_entitled: result.days_entitled || 0,
            is_determinant: result.is_determinant || false,
            rules: [],
            child_groups: [],
          };
          this.ruleGroups.push(newGroup);
        }
      });
  }

  editRuleGroup(group: RuleGroup, index: number): void {
    const dialogRef = this.dialog.open(ProtocolDialogComponent, {
      width: '700px',
      data: {
        title: 'Configuración del grupo',
        confirmText: 'Guardar cambios',
        cancelText: 'Cancelar',
        deleteText: 'Eliminar',
        isEditMode: true,
        initialName: group.name,
        initialOperator: group.operator,
        initialExemptWeeks: group.exempt_weeks,
        initialDaysEntitled: group.days_entitled,
        initialIsDeterminant: group.is_determinant,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: RuleGroupDialogResult | null) => {
        if (result) {
          if (result.action === 'delete') {
            this.removeRuleGroup(index);
          } else if (result.action === 'save') {
            group.name = result.name;
            group.operator = result.operator;
            group.exempt_weeks = result.exempt_weeks || 0;
            group.days_entitled = result.days_entitled || 0;
            group.is_determinant = result.is_determinant || false;
          }
        }
      });
  }

  removeRuleGroup(index: number): void {
    this.ruleGroups.splice(index, 1);
  }

  // Rule Management Methods
  addRule(group: RuleGroup): void {
    const newRule: Rule = {
      name: '',
      rule_type: '',
      error_message: '',
    };
    group.rules.push(newRule);
  }

  removeRule(group: RuleGroup, ruleIndex: number): void {
    group.rules.splice(ruleIndex, 1);
  }

  // Child Group Management Methods
  addChildGroup(parentGroup: RuleGroup): void {
    const dialogRef = this.dialog.open(ProtocolDialogComponent, {
      width: '700px',
      data: {
        title: 'Nuevo subgrupo de reglas',
        confirmText: 'Crear',
        cancelText: 'Cancelar',
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: RuleGroupDialogResult | null) => {
        if (result && result.action === 'save') {
          const newChildGroup: RuleGroup = {
            name: result.name,
            operator: result.operator,
            priority: parentGroup.priority - 10,
            exempt_weeks: result.exempt_weeks || 0,
            days_entitled: result.days_entitled || 0,
            is_determinant: result.is_determinant || false,
            rules: [],
            child_groups: [],
          };
          parentGroup.child_groups.push(newChildGroup);
        }
      });
  }

  editChildGroup(
    childGroup: RuleGroup,
    parentGroup: RuleGroup,
    childIndex: number
  ): void {
    const dialogRef = this.dialog.open(ProtocolDialogComponent, {
      width: '700px',
      data: {
        title: 'Configuración del grupo',
        confirmText: 'Guardar cambios',
        cancelText: 'Cancelar',
        deleteText: 'Eliminar',
        isEditMode: true,
        initialName: childGroup.name,
        initialOperator: childGroup.operator,
        initialExemptWeeks: childGroup.exempt_weeks,
        initialDaysEntitled: childGroup.days_entitled,
        initialIsDeterminant: childGroup.is_determinant,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: RuleGroupDialogResult | null) => {
        if (result) {
          if (result.action === 'delete') {
            this.removeChildGroup(parentGroup, childIndex);
          } else if (result.action === 'save') {
            childGroup.name = result.name;
            childGroup.operator = result.operator;
            childGroup.exempt_weeks = result.exempt_weeks || 0;
            childGroup.days_entitled = result.days_entitled || 0;
            childGroup.is_determinant = result.is_determinant || false;
          }
        }
      });
  }

  removeChildGroup(parentGroup: RuleGroup, childIndex: number): void {
    parentGroup.child_groups.splice(childIndex, 1);
  }

  // Helper methods for rule type fields
  needsValueField(ruleType: string): boolean {
    return [
      'max_weeks',
      'min_weeks',
      'min_consecutive_weeks',
      'max_consecutive_weeks',
      'max_weeks_per_month',
      'number_of_weeks_per_petition',
    ].includes(ruleType);
  }

  needsBooleanField(ruleType: string): boolean {
    return ruleType === 'single_days';
  }

  needsDateRangeField(ruleType: string): boolean {
    return [
      'blocked_range',
      'allowed_range',
      'min_consecutive_weeks',
      'max_consecutive_weeks',
      'max_weeks',
      'min_weeks',
    ].includes(ruleType);
  }

  getRuleTypeLabel(ruleType: string): string {
    const choice = this.ruleTypeChoices.find((c) => c.value === ruleType);
    return choice ? choice.label : ruleType;
  }

  getOperatorLabel(operator: 'AND' | 'OR'): string {
    return operator === 'AND'
      ? 'Se deben cumplir todas las reglas'
      : 'Se puede elegir cualquiera de las opciones';
  }

  // Get value field label based on rule type
  getValueFieldLabel(ruleType: string): string {
    switch (ruleType) {
      case 'max_weeks':
        return 'NÚMERO MÁXIMO DE SEMANAS DISPONIBLE A LA HORA DE ESCOGER LAS VACACIONES';
      case 'min_weeks':
        return 'NÚMERO MÍNIMO DE SEMANAS REQUERIDAS';
      case 'min_consecutive_weeks':
        return 'NÚMERO MÍNIMO DE SEMANAS CONSECUTIVAS';
      case 'max_consecutive_weeks':
        return 'NÚMERO MÁXIMO DE SEMANAS CONSECUTIVAS';
      case 'max_weeks_per_month':
        return 'NÚMERO MÁXIMO DE SEMANAS POR MES';
      case 'number_of_weeks_per_petition':
        return 'NÚMERO MÁXIMO DE SEMANAS POR PETICIÓN';
      default:
        return 'VALOR';
    }
  }

  deleteProtocol() {
    if (this.protocolId === null) {
      this.snackBar.open('No se puede eliminar: protocolo no guardado', 'Cerrar', {
        duration: 3000,
      });
      return;
    }

    const protocolIdToDelete = this.protocolId;
    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '550px',
      disableClose: true,
      data: {
        title: '¿Seguro que quieres eliminar este protocolo?',
        message: 'Asegúrate que no esté asignado a ninguna operativa.',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => {
          return this.holidaysService.deleteProtocol(protocolIdToDelete);
        },
      },
    });

    // Esperar a que el diálogo se cierre y redirigir
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.router.navigate([this.returnLink]);
      }
    });
  }

  // Simulate - Generate JSON for protocol import
  simulate(): void {
    const formValue = this.protocolForm.value;

    const protocolJson = {
      protocol: {
        name: formValue.name || '',
        code: formValue.code || '',
        description: formValue.description || '',
        enabled: formValue.enabled ?? true,
        rule_groups: this.ruleGroups.map((group) =>
          this.transformRuleGroupForExport(group)
        ),
      },
    };

    console.log(
      'Protocol JSON for import:',
      JSON.stringify(protocolJson, null, 2)
    );

    // Copy to clipboard
    navigator.clipboard.writeText(JSON.stringify(protocolJson, null, 2)).then(
      () => {
        this.snackBar.open('JSON copiado al portapapeles', 'Cerrar', {
          duration: 3000,
        });
      },
      () => {
        this.snackBar.open('Error al copiar al portapapeles', 'Cerrar', {
          duration: 3000,
        });
      }
    );
  }

  private transformRuleGroupForExport(group: RuleGroup): any {
    const exportGroup: any = {
      name: group.name,
      operator: group.operator,
      priority: group.priority,
    };

    // Only include days_entitled if it has a value > 0
    if (group.days_entitled !== undefined && group.days_entitled > 0) {
      exportGroup.days_entitled = group.days_entitled;
    }

    // Only include exempt_weeks if it has a value > 0
    if (group.exempt_weeks !== undefined && group.exempt_weeks > 0) {
      exportGroup.exempt_weeks = group.exempt_weeks;
    }

    // Only include is_determinant if it's true
    if (group.is_determinant === true) {
      exportGroup.is_determinant = group.is_determinant;
    }

    // Transform rules
    if (group.rules && group.rules.length > 0) {
      exportGroup.rules = group.rules
        .filter((rule) => rule.rule_type) // Only include rules with a type selected
        .map((rule) => this.transformRuleForExport(rule));
    }

    // Transform child groups recursively
    if (group.child_groups && group.child_groups.length > 0) {
      exportGroup.child_groups = group.child_groups.map((childGroup) =>
        this.transformRuleGroupForExport(childGroup)
      );
    }

    return exportGroup;
  }

  private transformRuleForExport(rule: Rule): any {
    const exportRule: any = {
      name: rule.name || this.getRuleTypeLabel(rule.rule_type),
      rule_type: rule.rule_type,
    };

    // Add value for numeric rules
    if (this.needsValueField(rule.rule_type) && rule.value !== undefined) {
      exportRule.value = rule.value;
    }

    // Add boolean_value for single_days
    if (this.needsBooleanField(rule.rule_type)) {
      exportRule.boolean_value = rule.boolean_value ?? false;
    }

    // Add date range fields
    if (this.needsDateRangeField(rule.rule_type)) {
      if (rule.date_range_start) {
        exportRule.date_range_start = this.formatDateForExport(
          rule.date_range_start
        );
      }
      if (rule.date_range_end) {
        exportRule.date_range_end = this.formatDateForExport(
          rule.date_range_end
        );
      }
    }

    // Add error message if provided
    if (rule.error_message) {
      exportRule.error_message = rule.error_message;
    }

    // Only include is_determinant if it's true
    if (rule.is_determinant === true) {
      exportRule.is_determinant = rule.is_determinant;
    }

    return exportRule;
  }

  private formatDateForExport(date: any): string {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
